import React, { useMemo } from "react";
import { Dialog, IconButton, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DraggableImageContainer from "./DraggableImage";
import VideoFrameScrubber from "./VideoFrameScrubber";

interface CustomImageViewerProps {
  open: boolean;
  src: string;
  alt?: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  initialTime?: number
}

const CustomImageViewer: React.FC<CustomImageViewerProps> = ({
  open,
  src,
  alt = "Image Viewer",
  onClose,
  onPrev,
  onNext,
  initialTime
}) => {
  const isVideo = useMemo(() => {
    const videoExtensions = [".mp4", ".webm", ".ogg"];
    return videoExtensions.some((ext) => src.toLowerCase().includes(ext));
  }, [src]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        id: "fullscreen-viewer",
        sx: {
          width: "auto",
          height: "auto",
          maxWidth: "90vw",
          maxHeight: "90vh",
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      }}
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        '& .MuiDialog-paper': {
          transform: "none !important",
        },
      }}
    >
      <Paper
        elevation={5}
        sx={{
          width: "80vw",
          height: "80vh",
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          margin: "auto",
          padding: 2
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 16, right: 16, color: "white", zIndex: 2 }}
        >
          <CloseIcon />
        </IconButton>

        {/* Prev Button */}
        {!isVideo && (
          <IconButton
            onClick={onPrev}
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              backgroundColor: "rgba(0,0,0,0.3)",
              zIndex: 2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            ◀
          </IconButton>
        )}

        {/* Next Button */}
        {!isVideo && (
          <IconButton
            onClick={onNext}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              backgroundColor: "rgba(0,0,0,0.3)",
              zIndex: 2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            ▶
          </IconButton>
        )}

        {isVideo ? 
          <VideoFrameScrubber filteredImages={[{src, idx: 1, imageType: "uc"}]} initialTime={initialTime} inModal/> : 
          <DraggableImageContainer src={src} alt={alt} />}
      </Paper>
    </Dialog>
  );
};

export default CustomImageViewer;
